/**
 * Session and tenant selection state.
 *
 * Kept as a small observable store rather than a global Redux-like container:
 * the workspace needs exactly one piece of truly global state (who is signed in
 * and which tenant is active). Remote list data stays in TanStack Query.
 *
 * Persistence note: the CSRF token returned by login is a double-submit token
 * bound to the HttpOnly session cookie, so it is kept in sessionStorage (cleared
 * when the tab closes) to survive a page refresh. No password or long-lived
 * credential is ever stored.
 */

import type { ApiError } from '../api/errors';
import type { SessionUser, Tenant } from '../api/types';

export type SessionStatus = 'restoring' | 'anonymous' | 'authenticated';

export type AuthProbeOutcome = 'ok' | 'unauthorized' | 'unavailable';

/**
 * Outcome of a sign-out attempt, as far as the UI can honestly report it.
 *
 *  - `revoked`     the API confirmed it invalidated the session token
 *  - `local-only`  the API could not be reached or refused; the cookie may still
 *                  be valid on the server, so this is NOT a completed sign-out
 */
export type SignOutOutcome = 'revoked' | 'local-only';

export interface SignOutResult {
  readonly outcome: SignOutOutcome;
  readonly error: ApiError | null;
}

export interface SessionSnapshot {
  readonly status: SessionStatus;
  readonly user: SessionUser | null;
  readonly csrfToken: string | null;
  readonly tenantId: string | null;
  readonly probeOutcome: AuthProbeOutcome | null;
  readonly probeError: ApiError | null;
}

const USER_STORAGE_KEY = 'yq.session.user';
const CSRF_STORAGE_KEY = 'yq.session.csrf';
const TENANT_STORAGE_KEY = 'yq.tenant.selected';

const EMPTY: SessionSnapshot = {
  status: 'restoring',
  user: null,
  csrfToken: null,
  tenantId: null,
  probeOutcome: null,
  probeError: null,
};

/**
 * Best-effort browser storage.
 *
 * Storage can legitimately be unavailable (privacy mode, blocked third-party
 * context, quota exhaustion). None of those should break sign-in, but they must not
 * be swallowed silently either: the failure is recorded in `persistenceDegraded` so
 * the shell can tell the operator that the session will not survive a refresh,
 * instead of the page reloading into an unexplained signed-out state.
 */
let persistenceDegraded = false;

function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    persistenceDegraded = true;
    return null;
  }
}

function writeStorage(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    persistenceDegraded = true;
  }
}

const sessionArea = (): Storage => window.sessionStorage;
const localArea = (): Storage => window.localStorage;

export function isPersistenceDegraded(): boolean {
  return persistenceDegraded;
}

function parseStoredUser(raw: string | null): SessionUser | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Record<string, unknown>)['id'] === 'string' &&
      typeof (parsed as Record<string, unknown>)['email'] === 'string'
    ) {
      const record = parsed as Record<string, unknown>;
      return {
        id: record['id'] as string,
        email: record['email'] as string,
        display_name: typeof record['display_name'] === 'string' ? record['display_name'] : '',
        status: typeof record['status'] === 'string' ? record['status'] : 'active',
        must_change_password: record['must_change_password'] === true,
        ...(typeof record['is_platform_operator'] === 'boolean'
          ? { is_platform_operator: record['is_platform_operator'] }
          : {}),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export class SessionStore {
  private snapshot: SessionSnapshot = EMPTY;
  private readonly listeners = new Set<() => void>();

  /**
   * Called whenever cached tenant-owned data must be dropped.
   *
   * The store does not own the query cache (that would invert the dependency
   * between session state and server state), so bootstrap registers a callback.
   * Keeping it here means every path that ends a session — explicit sign-out, a
   * 401, or a new sign-in — evicts cached rows through exactly one mechanism.
   */
  private onTenantDataInvalidated: ((scopeOnly?: boolean) => void) | null = null;

  constructor() {
    const user = parseStoredUser(readStorage(sessionArea, USER_STORAGE_KEY));
    const csrfToken = readStorage(sessionArea, CSRF_STORAGE_KEY);
    const tenantId = readStorage(localArea, TENANT_STORAGE_KEY);

    // A stored user means "this tab was signed in"; the session cookie still has
    // to be confirmed against the API before anything is treated as authorized.
    this.snapshot = {
      ...EMPTY,
      status: user === null ? 'restoring' : 'restoring',
      user,
      csrfToken,
      tenantId,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): SessionSnapshot => this.snapshot;

  /** Registers the cache-dropping hook; returns the previous one for teardown. */
  setTenantDataInvalidationHook(hook: ((scopeOnly?: boolean) => void) | null): void {
    this.onTenantDataInvalidated = hook;
  }

  private dropTenantData(scopeOnly = false): void {
    this.onTenantDataInvalidated?.(scopeOnly);
  }

  private emit(patch: Partial<SessionSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Records a confirmed sign-in and drops any rows cached for a previous user. */
  signIn(result: { csrfToken: string; user: SessionUser }): void {
    // Signing in as a different user must not inherit the previous user's rows.
    this.dropTenantData();
    writeStorage(sessionArea, USER_STORAGE_KEY, JSON.stringify(result.user));
    writeStorage(sessionArea, CSRF_STORAGE_KEY, result.csrfToken);
    this.emit({
      status: 'authenticated',
      user: result.user,
      csrfToken: result.csrfToken,
      probeOutcome: 'ok',
      probeError: null,
    });
  }

  updateUser(user: SessionUser): void {
    writeStorage(sessionArea, USER_STORAGE_KEY, JSON.stringify(user));
    this.emit({ user });
  }

  clearTenant(): void {
    writeStorage(localArea, TENANT_STORAGE_KEY, null);
    this.dropTenantData(true);
    this.emit({ tenantId: null });
  }

  /**
   * Clears local credentials **and** cached tenant rows.
   *
   * This is the local half of a sign-out. Whether the server revoked the session is
   * a separate question the caller learns from `POST /auth/logout`; when that call
   * cannot be made, `SignOutResult` says so and the UI must not claim a full
   * sign-out.
   *
   * The tenant *selection* is dropped here too. An earlier revision kept it as a
   * harmless "preference", but a remembered tenant id is per-browser state that
   * outlives the session: after one user signs out, the next person on the same tab
   * would silently land inside the previous user's tenant scope. The workspace
   * re-derives a sensible default from the new user's tenant list instead.
   */
  signOut(): void {
    writeStorage(sessionArea, USER_STORAGE_KEY, null);
    writeStorage(sessionArea, CSRF_STORAGE_KEY, null);
    writeStorage(localArea, TENANT_STORAGE_KEY, null);
    this.dropTenantData();
    this.emit({
      status: 'anonymous',
      user: null,
      csrfToken: null,
      tenantId: null,
      probeOutcome: null,
      probeError: null,
    });
  }

  /**
   * Called by the transport whenever any endpoint answers 401. Like sign-out, this
   * drops the tenant selection: the session that was allowed into that scope is
   * gone, so the next user must not inherit it.
   */
  handleUnauthorized = (): void => {
    if (this.snapshot.status === 'anonymous') return;
    writeStorage(sessionArea, USER_STORAGE_KEY, null);
    writeStorage(sessionArea, CSRF_STORAGE_KEY, null);
    writeStorage(localArea, TENANT_STORAGE_KEY, null);
    this.dropTenantData();
    this.emit({
      status: 'anonymous',
      user: null,
      csrfToken: null,
      tenantId: null,
      probeOutcome: 'unauthorized',
      probeError: null,
    });
  };

  /** Records the result of the sign-in probe performed at start-up. */
  resolveProbe(outcome: AuthProbeOutcome, error?: ApiError): void {
    if (outcome === 'ok') {
      this.emit({ status: 'authenticated', probeOutcome: 'ok', probeError: null });
      return;
    }
    if (outcome === 'unauthorized') {
      writeStorage(sessionArea, USER_STORAGE_KEY, null);
      writeStorage(sessionArea, CSRF_STORAGE_KEY, null);
      writeStorage(localArea, TENANT_STORAGE_KEY, null);
      this.dropTenantData();
      this.emit({
        status: 'anonymous',
        user: null,
        csrfToken: null,
        tenantId: null,
        probeOutcome: 'unauthorized',
        probeError: null,
      });
      return;
    }

    // The API could not be reached or the endpoint is missing. A remembered
    // identity keeps the shell usable so each page can report its own error;
    // otherwise the user is sent to the sign-in screen with a visible notice.
    this.emit({
      status: this.snapshot.user === null ? 'anonymous' : 'authenticated',
      probeOutcome: 'unavailable',
      probeError: error ?? null,
    });
  }

  /**
   * Switches the active tenant and evicts the previous tenant's rows.
   *
   * Both halves are required: the transport reads the tenant live, so the switch
   * alone would re-scope new requests, and the eviction stops the previous tenant's
   * rows from being rendered while the new scope loads. Requests already in flight
   * for the old tenant are dropped by the transport rather than being written into
   * the new tenant's cache entry.
   */
  setTenant(tenantId: string, tenants: readonly Tenant[]): void {
    const known = tenants.some((tenant) => tenant.id === tenantId);
    if (!known) return;

    const previous = this.snapshot.tenantId;
    if (previous === tenantId) return;

    writeStorage(localArea, TENANT_STORAGE_KEY, tenantId);
    this.emit({ tenantId });
    // After the id is published, so a listener that re-reads it sees the new scope.
    if (previous !== null) this.dropTenantData(true);
  }
}

export const sessionStore = new SessionStore();
